import { TreeNodeType, TreeRow } from "@/types/LCAoM/TreeBuilder";
import { NodeTypes, RemoveByNode, RemoveCategoryMaterial, RemoveMaterialsRequest, RemoveSupplementalsRequest } from "@api/apiClient";
import { useRemoveMaterials as useRemoveMaterialsMutation, useRemoveSupplementals as useRemoveSupplementalsMutation } from "@api/models";
import useSelectionManager from "@hooks/useSelectionManager";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, useCallback } from "react";

interface UseRemoveMaterialsParams {
  clearSelectedNodes: () => Promise<void>;
  assignGridApiRef: MutableRefObject<GridApiPremium>;
  /** Callback invoked after successful removal (e.g., to refetch data) */
  onSuccess?: () => void | Promise<void>;
}

interface UseRemoveMaterialsResult {
  handleRemoveMaterials: () => Promise<void>;
  isRemoveMaterialsPending: boolean;
  isRemoveSupplementalsPending: boolean;
}

export const useRemoveMaterials = ({
  clearSelectedNodes,
  assignGridApiRef,
  onSuccess,
}: UseRemoveMaterialsParams): UseRemoveMaterialsResult => {
  const selectionManager = useSelectionManager();
  const mutateRemoveMaterials = useRemoveMaterialsMutation();
  const mutateRemoveSupplementals = useRemoveSupplementalsMutation();

  const handleRemoveMaterials = useCallback(async () => {
    const scenarioId = selectionManager.selectedScenarioId;

    if (!scenarioId) {
      console.error("ScenarioId is required for removing materials");
      return;
    }

    // Get selected rows from the grid
    const selectedRowsMap = assignGridApiRef.current?.getSelectedRows();
    if (!selectedRowsMap || selectedRowsMap.size === 0) {
      console.warn("No rows selected for removal");
      return;
    }

    // Convert to TreeRow array
    const selectedRows: TreeRow[] = [];
    selectedRowsMap.forEach((row) => {
      selectedRows.push(row as TreeRow);
    });

    // Build categories and nodes arrays
    const categories: RemoveCategoryMaterial[] = [];
    const nodes: RemoveByNode[] = [];
    const modelIds = new Set<string>();

    // Track selected category IDs to exclude their children from the nodes array
    const selectedCategoryIds = new Set<string>();

    // First pass: identify selected category nodes
    selectedRows.forEach((row) => {
      if (row.type === TreeNodeType.Group && row.field === "cadModelCategoryId" && row.rawValue) {
        // This is a category node - add to categories array
        categories.push(
          new RemoveCategoryMaterial({
            scenarioId: scenarioId,
            cadModelCategoryId: row.rawValue,
          }),
        );
        selectedCategoryIds.add(row.rawValue);
      }
    });

    // Second pass: add element/component nodes that are not children of selected categories
    selectedRows.forEach((row) => {
      // Only process element/component nodes (not groups)
      if (row.type === TreeNodeType.Group) {
        return;
      }

      const nodeId = row.data?.nodeId;
      const nodeType = row.data?.nodeType;
      const cadModelCategoryId = row.data?.cadModelCategoryId;
      const modelId = row.data?.modelId;

      // Collect modelId from the row data
      if (modelId) {
        modelIds.add(modelId);
      }

      // Skip if this node belongs to a selected category (category will handle it)
      if (cadModelCategoryId && selectedCategoryIds.has(cadModelCategoryId)) {
        return;
      }

      if (nodeId != null && nodeType != null) {
        // Map numeric nodeType to string enum NodeTypes
        const nodeTypeMap: Record<number, NodeTypes> = {
          1: NodeTypes.Category,
          2: NodeTypes.Family,
          3: NodeTypes.Type,
          4: NodeTypes.Element,
          5: NodeTypes.Component,
          6: NodeTypes.Supplemental,
        };
        const mappedNodeType = nodeTypeMap[nodeType as number];

        if (mappedNodeType) {
          nodes.push(
            new RemoveByNode({
              scenarioId: scenarioId,
              nodeId: Number(nodeId),
              nodeType: mappedNodeType,
            }),
          );
        } else {
          console.warn(`Unknown nodeType value: ${nodeType}. Skipping node:`, row);
        }
      } else {
        console.warn("Node data missing nodeId or nodeType. Skipping node:", row);
      }
    });

    // Also collect modelIds from category rows (they might have modelId in their data)
    selectedRows.forEach((row) => {
      if (row.type === TreeNodeType.Group && row.data?.modelId) {
        modelIds.add(row.data.modelId);
      }
    });

    if (categories.length === 0 && nodes.length === 0) {
      console.warn("No valid categories or nodes found for removal");
      return;
    }

    if (modelIds.size === 0) {
      console.warn("No modelIds found in selected rows");
      return;
    }

    const removeSupplementalsRequest = new RemoveSupplementalsRequest({
      modelIds: Array.from(modelIds),
      categories,
      nodes: nodes,
    });

    await mutateRemoveSupplementals.mutateAsync(removeSupplementalsRequest as any);

    const removeMaterialsRequest = new RemoveMaterialsRequest({
      modelIds: Array.from(modelIds),
      categories,
      nodes: nodes,
    });

    await mutateRemoveMaterials.mutateAsync(removeMaterialsRequest as any);

    await clearSelectedNodes();
    await onSuccess?.();
  }, [assignGridApiRef, clearSelectedNodes, mutateRemoveMaterials, mutateRemoveSupplementals, onSuccess, selectionManager]);

  return {
    handleRemoveMaterials,
    isRemoveMaterialsPending: mutateRemoveMaterials.isPending,
    isRemoveSupplementalsPending: mutateRemoveSupplementals.isPending,
  };
};
